import React from 'react';
import { useTranslation } from 'react-i18next';
import './NativeBootScreen.css';

export type NativeBootPhase = 'enter' | 'hold' | 'exit';

interface NativeBootScreenProps {
  phase: NativeBootPhase;
}

type CatchphraseWord = { text: string; styled?: boolean };

const PAW_PRINTS = [
  { className: 'native-boot__paw--1', rotate: -18, delay: '0.55s' },
  { className: 'native-boot__paw--2', rotate: 14, delay: '0.7s' },
  { className: 'native-boot__paw--3', rotate: -10, delay: '0.85s' },
  { className: 'native-boot__paw--4', rotate: 20, delay: '1s' },
  { className: 'native-boot__paw--5', rotate: -6, delay: '1.15s' },
] as const;

const PawPrint: React.FC<{ className: string; rotate: number; delay: string }> = ({
  className,
  rotate,
  delay,
}) => (
  <svg
    className={`native-boot__paw ${className}`}
    viewBox="0 0 100 100"
    aria-hidden
    style={
      {
        '--paw-rotate': `${rotate}deg`,
        '--paw-delay': delay,
      } as React.CSSProperties
    }
  >
    <ellipse cx="50" cy="65" rx="18" ry="22" fill="currentColor" />
    <ellipse cx="28" cy="35" rx="10" ry="14" fill="currentColor" transform="rotate(-20 28 35)" />
    <ellipse cx="42" cy="25" rx="10" ry="14" fill="currentColor" transform="rotate(-5 42 25)" />
    <ellipse cx="58" cy="25" rx="10" ry="14" fill="currentColor" transform="rotate(5 58 25)" />
    <ellipse cx="72" cy="35" rx="10" ry="14" fill="currentColor" transform="rotate(20 72 35)" />
  </svg>
);

const WaveLayer: React.FC<{ className: string; path: string; fill: string }> = ({
  className,
  path,
  fill,
}) => (
  <svg
    className={`native-boot__wave ${className}`}
    viewBox="0 0 1440 200"
    preserveAspectRatio="none"
    aria-hidden
  >
    <path d={path} fill={fill} />
  </svg>
);

/**
 * Écran de boot natif — catchphrase hero Rintintin booking.
 */
const NativeBootScreen: React.FC<NativeBootScreenProps> = ({ phase }) => {
  const { t: tNative } = useTranslation('native', { keyPrefix: 'boot' });
  const { t: tCommon } = useTranslation('common');
  const catchphraseWordsRaw = tCommon('homeClassic.hero.titleWords', {
    returnObjects: true,
  });
  const catchphraseWords = Array.isArray(catchphraseWordsRaw)
    ? (catchphraseWordsRaw as CatchphraseWord[])
    : [{ text: 'Rintintin' }];

  const catchphraseText = catchphraseWords.map((w) => w.text).join(' ');

  return (
    <div
      className={`native-boot native-boot--${phase}`}
      role="status"
      aria-live="polite"
      aria-busy={phase !== 'exit'}
      aria-label={tNative('loadingAria')}
    >
      <div className="native-boot__backdrop" aria-hidden />
      <div className="native-boot__blob native-boot__blob--1" aria-hidden />
      <div className="native-boot__blob native-boot__blob--2" aria-hidden />
      <div className="native-boot__blob native-boot__blob--3" aria-hidden />
      <div className="native-boot__glow native-boot__glow--1" aria-hidden />
      <div className="native-boot__glow native-boot__glow--2" aria-hidden />

      <WaveLayer
        className="native-boot__wave--top"
        fill="rgba(246, 198, 116, 0.22)"
        path="M0,96 C240,16 480,160 720,80 C960,0 1200,128 1440,64 L1440,0 L0,0 Z"
      />
      <WaveLayer
        className="native-boot__wave--mid"
        fill="rgba(238, 159, 153, 0.14)"
        path="M0,120 C320,40 640,168 960,88 C1200,32 1320,104 1440,72 L1440,200 L0,200 Z"
      />
      <WaveLayer
        className="native-boot__wave--bottom"
        fill="rgba(165, 118, 92, 0.1)"
        path="M0,128 C360,48 720,176 1080,96 C1260,56 1380,112 1440,88 L1440,200 L0,200 Z"
      />

      {PAW_PRINTS.map((paw) => (
        <PawPrint
          key={paw.className}
          className={paw.className}
          rotate={paw.rotate}
          delay={paw.delay}
        />
      ))}

      <div className="native-boot__content">
        <h1 className="native-boot__catchphrase" aria-label={catchphraseText}>
          {catchphraseWords.map((word, index) => {
            const lineBreak = word.styled && index > 0;
            return (
              <React.Fragment key={`${word.text}-${index}`}>
                {lineBreak ? <span className="native-boot__catchphrase-break" aria-hidden /> : null}
                <span
                  className={`native-boot__catchphrase-word${
                    word.styled ? ' native-boot__catchphrase-word--styled' : ''
                  }`}
                  style={{ '--word-delay': `${0.15 + index * 0.1}s` } as React.CSSProperties}
                >
                  {word.text}
                </span>
              </React.Fragment>
            );
          })}
        </h1>

        <div className="native-boot__progress" aria-hidden>
          <div className="native-boot__progress-track">
            <div className="native-boot__progress-fill">
              <div className="native-boot__progress-wave" />
            </div>
            <div className="native-boot__progress-shimmer" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NativeBootScreen;
